import React from "react";
import { motion } from "framer-motion";
import { Phone, FileText, Sparkles, ShieldCheck } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const options = [
  {
    title: "Apply on WhatsApp",
    desc: "Best for quick application and easy conversation with our team.",
    buttonText: "Open WhatsApp",
    href: "https://wa.me/919665855163?text=Hi%20A2Z%20Consultancy,%20I%20am%20looking%20for%20work.",
    icon: FaWhatsapp,
    type: "whatsapp",
    badge: "Fastest",
  },
  {
    title: "Call Us Directly",
    desc: "Call us if you want to speak directly and enquire about available work.",
    buttonText: "Call Now",
    href: "tel:+919665855163",
    icon: Phone,
    type: "call",
    badge: "Direct",
  },
  {
    title: "Fill Quick Form",
    desc: "Share your details through a detailed form and our team will contact you.",
    buttonText: "Open Form",
    href: "https://forms.gle/EiqVhxZbkbqaGYZm9",
    icon: FileText,
    type: "form",
    badge: "Detailed",
  },
];

const getCardClasses = (type) => {
  if (type === "whatsapp") {
    return {
      card: "bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white border-transparent",
      glow: "from-white/12 via-transparent to-white/8",
      chip: "bg-white/18 text-white border-white/20",
    };
  }

  if (type === "call") {
    return {
      card: "bg-gradient-to-br from-sky-500 to-indigo-600 text-white border-transparent",
      glow: "from-white/12 via-transparent to-white/8",
      chip: "bg-white/18 text-white border-white/20",
    };
  }

  return {
    card: "bg-gradient-to-br from-orange-400 to-amber-500 text-white border-transparent",
    glow: "from-white/12 via-transparent to-white/8",
    chip: "bg-white/18 text-white border-white/20",
  };
};

const JobsContactOptions = () => {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#f8fbff_0%,#ffffff_48%,#fff9f4_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_20%,rgba(56,189,248,0.12),transparent_24%),radial-gradient(circle_at_84%_22%,rgba(99,102,241,0.08),transparent_20%),radial-gradient(circle_at_82%_82%,rgba(251,146,60,0.12),transparent_24%)]" />

      {/* Blobs */}
      <div className="absolute top-[-100px] right-[-80px] w-[240px] h-[240px] bg-sky-200/35 rounded-full blur-3xl opacity-70" />
      <div className="absolute bottom-[-100px] left-[-80px] w-[240px] h-[240px] bg-orange-200/35 rounded-full blur-3xl opacity-70" />

      {/* Texture */}
      <div className="absolute inset-0 opacity-[0.05] [background-image:radial-gradient(#0f172a_0.8px,transparent_0.8px)] [background-size:18px_18px]" />

      {/* Top wave */}
      <svg
        className="absolute top-0 left-0 w-full h-[90px] md:h-[120px] opacity-60"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="rgba(255,255,255,0.68)"
          d="M0,128L60,122.7C120,117,240,107,360,117.3C480,128,600,160,720,165.3C840,171,960,149,1080,128C1200,107,1320,85,1380,74.7L1440,64L1440,0L0,0Z"
        />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="text-center mb-14 md:mb-16"
        >
          <span className="inline-flex items-center rounded-full border border-sky-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-sky-700 font-medium mb-5 shadow-sm">
            Contact Us Easily
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Apply Now
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Pick the method that feels easiest for you and contact us today.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-5 md:gap-6">
          {options.map((option, i) => {
            const Icon = option.icon;
            const isExternal = option.href.startsWith("http");
            const styles = getCardClasses(option.type);

            return (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -6 }}
                className={`group relative rounded-[28px] p-6 md:p-7 shadow-[0_12px_35px_rgba(0,0,0,0.10)] hover:shadow-[0_18px_45px_rgba(0,0,0,0.16)] transition-all duration-300 overflow-hidden flex flex-col h-full ${styles.card}`}
              >
                {/* Hover glow */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-300 bg-gradient-to-br ${styles.glow} pointer-events-none`}
                />

                {/* Dotted art */}
                <div className="absolute top-3 right-3 opacity-[0.12]">
                  <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                    {Array.from({ length: 5 }).map((_, row) =>
                      Array.from({ length: 5 }).map((__, col) => (
                        <circle
                          key={`${row}-${col}`}
                          cx={7 + col * 11}
                          cy={7 + row * 11}
                          r="2"
                          fill="white"
                        />
                      ))
                    )}
                  </svg>
                </div>

                {/* Top row */}
                <div className="relative flex items-start justify-between gap-3 mb-5">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center bg-white/20 border border-white/15 group-hover:scale-105 transition duration-300">
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${styles.chip}`}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {option.badge}
                  </span>
                </div>

                {/* Content */}
                <h3 className="relative text-xl md:text-2xl font-semibold mb-3 text-white">
                  {option.title}
                </h3>

                <p className="relative text-sm md:text-[15px] leading-6 mb-6 text-white/90">
                  {option.desc}
                </p>

                {/* Mid strip */}
                <div className="relative rounded-2xl border border-white/15 bg-white/10 px-4 py-3 mb-6">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-white/85">Best for</span>
                    <span className="text-sm font-semibold text-white">
                      {option.badge}
                    </span>
                  </div>
                </div>

                {/* Push button to bottom */}
                <div className="mt-auto">
                  <a
                    href={option.href}
                    target={isExternal ? "_blank" : "_self"}
                    rel={isExternal ? "noreferrer" : undefined}
                    className="relative block"
                  >
                    <button className="w-full py-3.5 md:py-4 rounded-2xl font-semibold transition-all duration-300 bg-white text-gray-900 hover:shadow-lg hover:scale-[1.02]">
                      {option.buttonText}
                    </button>
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom trust strip */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="mt-12 md:mt-14"
        >
          <div className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white/75 backdrop-blur-xl shadow-[0_12px_35px_rgba(15,23,42,0.06)] px-5 py-5 md:px-6">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(56,189,248,0.10),transparent_24%),radial-gradient(circle_at_82%_80%,rgba(251,146,60,0.10),transparent_24%)]" />

            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-green-50 to-sky-50 border border-green-100 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                </div>

                <div>
                  <p className="text-sm font-semibold text-indigo-700 mb-1">
                    Verified and trusted process
                  </p>
                  <p className="text-sm md:text-base text-gray-600">
                    Contact us through the method you prefer. We guide you through a simple and verified process.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-2 rounded-full border border-green-100 bg-green-50 text-green-700 text-sm font-medium">
                  WhatsApp Friendly
                </span>
                <span className="px-3 py-2 rounded-full border border-sky-100 bg-sky-50 text-sky-700 text-sm font-medium">
                  Easy Contact
                </span>
                <span className="px-3 py-2 rounded-full border border-orange-100 bg-orange-50 text-orange-700 text-sm font-medium">
                  Quick Reply
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default JobsContactOptions;